import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { CheckCircle, BookOpenCheck, Download, Library, X } from 'lucide-react'

interface TakeBookSuccessDialogProps {
  open: boolean
  onClose: () => void
  onReadNow: () => void
  onDownload: () => void
  onGoToLibrary: () => void
  bookTitle: string
  takesRemaining: number
  isDownloading?: boolean
}

export function TakeBookSuccessDialog({
  open,
  onClose,
  onReadNow,
  onDownload,
  onGoToLibrary,
  bookTitle,
  takesRemaining,
  isDownloading = false
}: TakeBookSuccessDialogProps) {
  const { t } = useTranslation('common')

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="take-success-dialog sm:max-w-md">
        <button
          onClick={onClose}
          className="close-btn absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{t('common.close')}</span>
        </button>
        
        <DialogHeader className="text-center pb-2">
          <div className="success-icon mx-auto w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
            <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <DialogTitle className="text-lg font-semibold">
            {t('library.bookAddedToLibrary', 'Book added to your library')}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
            {bookTitle}
          </p>
        </DialogHeader>

        <div className="actions-container space-y-3 pt-2">
          {/* Read Now - Primary action */}
          <Button
            onClick={onReadNow}
            className="read-btn w-full"
            size="lg"
          >
            <BookOpenCheck className="mr-2 h-5 w-5" />
            {t('library.readNow', 'Read Now')}
          </Button>

          {/* Secondary actions row */}
          <div className="secondary-actions flex gap-2">
            <Button
              onClick={onDownload}
              variant="outline"
              className="download-btn flex-1"
              disabled={isDownloading}
            >
              <Download className="mr-2 h-4 w-4" />
              {isDownloading ? t('library.downloading') : t('common.download')}
            </Button>
            <Button
              onClick={onGoToLibrary}
              variant="outline"
              className="library-btn flex-1"
            >
              <Library className="mr-2 h-4 w-4" />
              {t('library.toLibrary', 'To Library')}
            </Button>
          </div>
        </div>

        {/* Takes remaining info */}
        <div className="takes-info text-center pt-3 border-t mt-3">
          <p className="text-xs text-muted-foreground">
            {t('library.takesRemaining', '{{count}} takes left this month', { count: takesRemaining })}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

